/**
 * 平台级上下文：启动时拉一次 /api/bootstrap（公开设置 + 首页配置 + 游戏列表 + 种子状态），
 * 之后按 updatedAt 做协商缓存 —— 后台改完设置，前台不需要刷新就能拿到新配置。
 *
 * 首屏策略（这一版的重点）：
 * 1. 挂载时先用 localStorage 里的上次配置渲染（`?since=` / ETag 命中 304 时不重复下载），
 *    这样即使 API 慢半拍也不会先白屏再闪一下；
 * 2. 同时打一次 /api/bootstrap 拿权威数据 + 种子状态 + 上贡次数；
 * 3. 之后在窗口重新聚焦时（以及每 60 秒）用 GET /api/site-config?since=<updatedAt> 轻量校验，
 *    服务端没变就只花一个 304。
 *
 * 游戏列表完全由后台配置驱动（出厂默认来自 games.json）—— 新增游戏 = 后台加一条 + 加一个页面。
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { DEFAULT_PROFILE, type SiteGame } from "@baibaisex/shared";
import { api, errorText, type BootstrapData, type SiteConfigData } from "./api.ts";

const CACHE_KEY = "baibaisex:site-config:v1";
/** 配置校验间隔：后台刚保存完也会主动 reload，这里只是兜底 */
const REVALIDATE_MS = 60_000;

interface PlatformValue {
  /** 仅在「第一次拿配置」时为 true；后台保存后的静默刷新不会把它打回 true */
  loading: boolean;
  /** 已经拿到过配置、正在后台刷新（用于按钮转圈） */
  refreshing: boolean;
  error: string | null;
  settings: BootstrapData["settings"];
  games: SiteGame[];
  seeded: boolean;
  /** 首页「模拟上贡」累计次数（服务端计数） */
  tributes: number;
  /** 设置版本戳：本地缓存的比对基准 */
  configUpdatedAt: number;
  /** 后台上传图片的体积上限 / 当前存储后端（前台提示文案用） */
  mediaMaxBytes: number;
  mediaBackend: string;
  /** 点一次上贡，返回最新累计值；后台关掉入口时抛错 */
  sendTribute: () => Promise<number>;
  reload: () => Promise<void>;
  /** 轻量校验（不做 loading 动画）；服务端 304 时什么都不做 */
  revalidate: () => Promise<void>;
}

const FALLBACK: PlatformValue["settings"] = {
  ageGate: true,
  adultMode: false,
  skipDailyLimit: 3,
  announcement: "",
  announcementEnabled: true,
  handicapAllowed: true,
  // bootstrap 还没回来时先用默认首页，避免首屏空白
  profile: DEFAULT_PROFILE,
};

const PlatformContext = createContext<PlatformValue>({
  loading: true,
  refreshing: false,
  error: null,
  settings: FALLBACK,
  games: [],
  seeded: false,
  tributes: 0,
  configUpdatedAt: 0,
  mediaMaxBytes: 2 * 1024 * 1024,
  mediaBackend: "",
  sendTribute: async () => 0,
  reload: async () => {},
  revalidate: async () => {},
});

/* ------------------------------------------------------------ 本地缓存 */

/** 只缓存「配置」本身（seed 状态与上贡次数每次都要新的，不进缓存） */
type CachedConfig = SiteConfigData;

function readCache(): CachedConfig | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedConfig>;
    if (!parsed || typeof parsed.updatedAt !== "number" || !parsed.settings || !Array.isArray(parsed.games)) {
      return null;
    }
    return parsed as CachedConfig;
  } catch {
    // 缓存坏了就当没有：不能因为一份脏缓存让整个前台起不来
    return null;
  }
}

function writeCache(data: BootstrapData | CachedConfig): void {
  try {
    const { updatedAt, settings, games, tiers, mediaMaxBytes, mediaBackend } = data as SiteConfigData;
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ updatedAt, settings, games, tiers, mediaMaxBytes, mediaBackend }),
    );
  } catch {
    // 隐私模式/配额满：缓存失败不影响功能
  }
}

export function PlatformProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tributes, setTributes] = useState(0);

  // 用 ref 读「当前有没有数据」，reload 才能保持引用稳定（AgeGate 等消费者依赖 loading 语义）
  const dataRef = useRef<BootstrapData | null>(null);
  dataRef.current = data;

  /** 最近一次拿到的配置（可能来自 localStorage 缓存）；协商缓存用它当基准 */
  const cacheRef = useRef<CachedConfig | null>(null);

  // 首次渲染前同步读一次缓存：直接在 state 初始化里用，避免多一次「空 → 有」的闪烁
  const [hydrated, setHydrated] = useState<CachedConfig | null>(() => {
    const hit = readCache();
    cacheRef.current = hit;
    return hit;
  });

  // loading 的真实语义是「还没有任何配置可渲染」：命中缓存时首屏就不该再停在「正在唤醒…」。
  // （曾经的 bug：只有 first===true 才 setLoading(false)，于是老访客带着缓存进来会永远卡在加载文案）
  const [loading, setLoading] = useState(() => cacheRef.current === null);

  const reload = useCallback(async () => {
    // 已经有配置时只做静默刷新：不能把 loading 打回 true，
    // 否则 AgeGate 会把整个应用卸载成「正在唤醒小游戏们…」，
    // 后台刚保存完弹出的「已保存」提示会被立刻清掉（用户以为没保存成功）。
    const hasConfig = dataRef.current !== null || cacheRef.current !== null;
    if (hasConfig) setRefreshing(true);
    else setLoading(true);
    try {
      const next = await api.bootstrap();
      setData(next);
      setHydrated(null);
      cacheRef.current = next;
      writeCache(next);
      setTributes(next.tributes ?? 0);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const revalidate = useCallback(async () => {
    const base = dataRef.current ?? cacheRef.current;
    if (!base) return;
    try {
      const result = await api.siteConfig(base.updatedAt);
      if (result.notModified || !result.data) return;
      const fresh = result.data;
      writeCache(fresh);
      cacheRef.current = fresh;
      // updatedAt 变了要把新配置并进来；seed / 上贡次数保持当前值（它们不在 site-config 里）
      setData((current) => (current ? { ...current, ...fresh } : current));
      setHydrated((current) => (dataRef.current ? current : fresh));
    } catch {
      // 静默失败：下一次聚焦/轮询再试，不打扰用户
    }
  }, []);

  const sendTribute = useCallback(async (): Promise<number> => {
    const result = await api.tribute();
    setTributes(result.tributes ?? 0);
    return result.tributes ?? 0;
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // 窗口重新聚焦 + 定时轮询：后台改完设置，用户切回来就能看到新的
  useEffect(() => {
    const onFocus = (): void => void revalidate();
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(onFocus, REVALIDATE_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
  }, [revalidate]);

  const source: BootstrapData | CachedConfig | null = data ?? hydrated;

  return (
    <PlatformContext.Provider
      value={{
        loading,
        refreshing,
        error,
        settings: source?.settings ?? FALLBACK,
        games: source?.games ?? [],
        seeded: data?.seeded ?? false,
        tributes,
        configUpdatedAt: source?.updatedAt ?? 0,
        mediaMaxBytes: source?.mediaMaxBytes ?? 2 * 1024 * 1024,
        mediaBackend: source?.mediaBackend ?? "",
        sendTribute,
        reload,
        revalidate,
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform(): PlatformValue {
  return useContext(PlatformContext);
}
